import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  InputSignal,
  ResourceRef,
  Signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { MatDialogActions, MatDialogClose, MatDialogContent } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { AlertComponent, ButtonComponent, IconButtonComponent } from '@libs/common/components';
import { DialogService } from '@libs/shared/common';
import { DeliveryWindowItem } from '@libs/shared/models';
import { DeliveryInfoService, ViewportService } from '@libs/shared/services';
import { TrackingGoogleService } from '@libs/shared/tracking';
import { DateTime } from 'luxon';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { DeliveryWindowsFormComponent } from '../../delivery-windows-form/delivery-windows-form.component';

@Component({
  selector: 'ui-delivery-windows-dialog',
  imports: [
    IconButtonComponent,
    MatDialogContent,
    DeliveryWindowsFormComponent,
    AlertComponent,
    MatDialogActions,
    ButtonComponent,
    MatDialogClose,
    NgxSkeletonLoaderModule,
    MatTooltip,
  ],
  templateUrl: './delivery-windows-dialog.component.html',
  styleUrl: './delivery-windows-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveryWindowsDialogComponent {
  public refDate: InputSignal<DateTime> = input.required();
  public deliveryWindowResource: ResourceRef<DeliveryWindowItem[] | undefined> = rxResource({
    params: () => this.refDate(),
    stream: ({ params: refDate }) => this.deliveryInfoService.getDeliveryWindows(refDate),
  });

  public dialogTemplate: Signal<TemplateRef<unknown>> = viewChild.required('dialogTemplate', { read: TemplateRef });
  public isMobile: Signal<boolean> = toSignal(this.viewportService.isMobile(), { initialValue: false });

  // in this dialog we only show the first delivery date of each category, so we need to filter the list accordingly
  public mappedDeliveryWindows: Signal<DeliveryWindowItem[]> = computed(() => {
    const windows: DeliveryWindowItem[] = this.deliveryWindowResource.value() ?? [];
    const sortedWindows: DeliveryWindowItem[] = windows
      .sort((a: DeliveryWindowItem, b: DeliveryWindowItem) => a.date.toMillis() - b.date.toMillis());
    const seenCategories: Set<string> = new Set<string>();
    const firstDeliveryDateByCategory: DeliveryWindowItem[] = [];

    for (const window of sortedWindows) {
      if (seenCategories.has(window.category)) {
        continue;
      }

      seenCategories.add(window.category);
      firstDeliveryDateByCategory.push(window);
    }

    return firstDeliveryDateByCategory;
  });

  public constructor(
    private readonly deliveryInfoService: DeliveryInfoService,
    private readonly dialogService: DialogService,
    private readonly viewportService: ViewportService,
    private readonly trackingService: TrackingGoogleService) {
  }

  public open(): void {
    this.deliveryWindowResource.reload();
    this.dialogService.open(this.dialogTemplate(),
      this.isMobile() ? {
        minWidth: 'calc(100vw - 0.75rem)',
        minHeight: '300px',
        panelClass: 'flex-dialog-container',
        restoreFocus: false,
        position: {
          top: 'calc(15vh + env(safe-area-inset-top))',
        },
      } : {
        minWidth: '500px',
        minHeight: '300px',
        panelClass: 'flex-dialog-container',
        restoreFocus: false,
        position: {
          top: 'calc(15vh + env(safe-area-inset-top))',
        },
      });

    this.trackingService.trackDeliveryWindowsDialogOpen();
  }
}
